/**
 * Shared helpers for the Cached Performance tab components (CR-4751).
 */
import {
  STATE_METRICS, type MetricType, type MetricUnit, type TrendPoint, type DashboardSnapshot,
} from '../../../lib/api/dashboard-snapshots'
import type { KPITargetRule } from '../../../lib/api/performanceSettings'
import { csvBlob } from '../../../utils/fileDownload'

export const METRIC_LABELS: Record<MetricType, string> = {
  open_order_value: 'Open Order Value',
  total_spend: 'Total Spend',
  monthly_spend: 'Monthly Spend',
  arrears_value: 'Arrears Value',
  arrears_count: 'Arrears Count',
  arrears_opening_value: 'Arrears Opening',
  arrears_new_value: 'New Arrears',
  arrears_resolved_value: 'Resolved Arrears',
  arrears_closing_value: 'Arrears Closing',
  otif_percentage: 'OTIF %',
  otd_percentage: 'OTD %',
  on_time_count: 'On-Time Count',
  early_count: 'Early Count',
  late_count: 'Late Count',
  total_grns: 'Total GRNs',
  ppm: 'PPM',
  ncr_count: 'NCR Count',
  ncr_open_count: 'Open NCRs',
  active_supplier_count: 'Active Suppliers',
  appeals_count: 'Appeals',
}

/**
 * CR-4751: the metrics where a fall is good news. The Period Comparison
 * coloured every rise green, so arrears or PPM going up read as an improvement.
 */
export const LOWER_IS_BETTER: MetricType[] = [
  'ppm', 'ncr_count', 'ncr_open_count', 'appeals_count', 'late_count',
  'arrears_value', 'arrears_count', 'arrears_opening_value', 'arrears_new_value', 'arrears_closing_value',
]

export interface DeltaTone {
  /** Which way the number moved. */
  direction: 'up' | 'down' | 'flat'
  /** Whether that is good news; null when it did not move. */
  good: boolean | null
}

/**
 * CR-4751: how a change reads — the arrow follows the number, the colour says
 * whether that is good news. Appeals falling 100% showed a green UP arrow.
 *
 * @param change - the signed change, on any scale (a % delta, or current − 0)
 * @param lowerIsBetter - a fall is the good direction (see LOWER_IS_BETTER)
 * @returns the direction and whether it is good news
 */
export function deltaTone(change: number, lowerIsBetter: boolean): DeltaTone {
  if (change === 0 || !Number.isFinite(change)) return { direction: 'flat', good: null }
  return { direction: change > 0 ? 'up' : 'down', good: lowerIsBetter ? change < 0 : change > 0 }
}

export function asNumber(v: string | number | null | undefined): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

export function percentDelta(current: number, previous: number): number | null {
  if (previous === 0 || Number.isNaN(previous)) return null
  return ((current - previous) / previous) * 100
}

/**
 * A snapshot value in its unit.
 *
 * @param value - the stored value
 * @param unit - its unit
 * @param formatCurrency - money formatter (compact or full, the caller's choice)
 * @param formatNumber - CR-4751: the user's number format (useFormatters); the
 *   browser locale grouped 1716667 as "17,16,667"
 * @returns the display string, or an em dash for no value
 */
export function formatValueByUnit(
  value: string | number | null | undefined,
  unit: MetricUnit,
  formatCurrency: (n: number) => string,
  formatNumber: (n: number) => string,
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return '—'
  if (unit === 'currency') return formatCurrency(n)
  if (unit === 'percentage') return `${n.toFixed(1)}%`
  if (unit === 'ppm') return `${formatNumber(Math.round(n))} ppm`
  return formatNumber(Math.round(n))
}

/** CR-4751: a period metric with no row for the latest capture — nothing was measured. */
export const NA_LABEL = 'N/A'
export const NA_REASON = 'Nothing to measure this period'

/** CR-4751: shown where the Daily view hides OTIF, OTD and Active Suppliers. */
export const DAILY_HIDDEN_NOTE =
  'OTIF, OTD and Active Suppliers are measured per week, month or quarter — choose Weekly or longer.'

/**
 * CR-4751: the period metrics with nothing to measure in the latest capture —
 * no row on its date — shown as N/A. `latest` returns each metric's newest
 * row, so without this a quarter with no PPM showed the PREVIOUS quarter's.
 * Balances (open orders, arrears) are never N/A.
 *
 * @param latest - the selected period's latest snapshots
 * @param metrics - the metrics to judge
 * @param period - the selected period; Monthly Spend is never N/A on daily / weekly
 * @returns the metrics to show as N/A (empty before any capture)
 */
export function notApplicableMetrics(
  latest: DashboardSnapshot[] | undefined, metrics: MetricType[], period?: string,
): Set<MetricType> {
  const out = new Set<MetricType>()
  if (!latest?.length) return out
  const captured = latest.reduce((max, s) => (s.snapshot_date > max ? s.snapshot_date : max), '')
  const byMetric = buildLatestMap(latest)
  // On a daily or weekly row Monthly Spend is month-to-date — held back from
  // `latest` as a partial period, not missing for want of anything to measure.
  const partialHere: MetricType[] = period === 'daily' || period === 'weekly' ? ['monthly_spend'] : []
  metrics.forEach((m) => {
    if (STATE_METRICS.includes(m) || partialHere.includes(m)) return
    const s = byMetric.get(m)
    if (!s || s.snapshot_date < captured) out.add(m)
  })
  return out
}

// CR-6307: accepts the preference-aware formatter (useFormatters().formatDate)
// so callers render the user's date_format with no day-shift; the hardcoded
// en-GB path remains only as a no-context fallback.
export function formatDate(
  iso: string | null | undefined,
  fmt?: (d: string | Date) => string,
): string {
  if (!iso) return '—'
  if (fmt) return fmt(iso)
  // eslint-disable-next-line no-restricted-syntax -- CR-6307: no-context fallback only
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

export interface ChartPoint {
  month: string
  value: number
  /** CR-6266: the target in force on the snapshot's date (see withTargets). */
  target?: number
}

/**
 * CR-6266: the target in force on a date — the target range covering it,
 * else the default target.
 *
 * @param ranges - one KPI's target ranges (none: the default applies)
 * @param fallback - the KPI's default target
 * @param isoDate - the date, YYYY-MM-DD (a longer ISO string is trimmed)
 * @returns the target for that date
 */
export function targetOn(ranges: KPITargetRule[] | undefined, fallback: number, isoDate: string): number {
  const day = isoDate.slice(0, 10)
  const rule = (ranges ?? []).find((r) => r.effective_from <= day && (!r.effective_to || day <= r.effective_to))
  return rule ? Number(rule.target_value) : fallback
}

/**
 * CR-6266: a chart series with each point's target, from the snapshot it
 * came from. With no target at all the series is returned unchanged.
 *
 * @param series - the chart points (from toChartSeries)
 * @param points - the snapshots the series was built from, same order
 * @param ranges - the KPI's target ranges
 * @param fallback - the KPI's default target, or null when unknown
 * @returns the series with `target` on every point
 */
export function withTargets(
  series: ChartPoint[], points: TrendPoint[], ranges: KPITargetRule[] | undefined, fallback: number | null,
): ChartPoint[] {
  if (fallback === null) return series
  return series.map((pt, i) => ({ ...pt, target: targetOn(ranges, fallback, pointDate(points[i])) }))
}

/**
 * CR-4751: the date a trend point is ABOUT — the first day of the period it
 * measures. September's monthly snapshot is stamped 1 October, so labelling by
 * `snapshot_date` put every month one month late.
 *
 * @param p - a trend point from the trends endpoint
 * @returns its period's first day (YYYY-MM-DD); `snapshot_date` for a point
 *   built without a window
 */
export function pointDate(p: TrendPoint): string {
  return p.window_start ?? p.snapshot_date
}

type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly'

/**
 * Period-aware label so the x-axis doesn't collapse multiple points in the
 * same month to the same text.
 *   daily     → "23 Apr"
 *   weekly    → "13 Apr" (Monday of the week)
 *   monthly   → "Feb 26"
 *   quarterly → "Q1 26"
 */
export function formatChartLabel(iso: string, period: Granularity): string {
  const d = new Date(iso)
  switch (period) {
    case 'daily':
    case 'weekly':
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    case 'monthly':
      return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
    case 'quarterly': {
      const q = Math.floor(d.getMonth() / 3) + 1
      const yr = String(d.getFullYear()).slice(2)
      return `Q${q} ${yr}`
    }
  }
}

/** Turn a trend series into recharts-friendly rows keyed by period-aware label. */
export function toChartSeries(points: TrendPoint[], period: Granularity = 'monthly'): ChartPoint[] {
  return points.map((p) => ({
    month: formatChartLabel(pointDate(p), period),
    value: asNumber(p.metric_value),
  }))
}

/**
 * Combine multiple trend series into one recharts-friendly row set, one row per
 * period in date order. CR-4751: rows used to come out in first-seen order, so
 * a series that starts late (open orders, captured nightly only) put its months
 * before the backfilled ones — "Jul 26, Sept 26, Oct 25 … Jun 26".
 */
export function mergeSeries(
  series: Array<{ key: string; points: TrendPoint[] }>,
  period: Granularity = 'monthly',
): Array<Record<string, string | number>> {
  const byDate = new Map<string, Record<string, string | number>>()
  series.forEach((s) => {
    s.points.forEach((p) => {
      const date = pointDate(p)
      const row = byDate.get(date) ?? { month: formatChartLabel(date, period) }
      row[s.key] = asNumber(p.metric_value)
      byDate.set(date, row)
    })
  })
  return Array.from(byDate.keys()).sort().map((date) => byDate.get(date) as Record<string, string | number>)
}

/** CSV escape + download helper. */
export function downloadCSV(rows: string[][], filename: string): void {
  const csv = rows
    .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = csvBlob(csv) // CR-8703: BOM, so Excel reads UTF-8
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * One dropdown, one axis: period granularity. The lookback window is derived
 * from the period (e.g. monthly → 12 months back, weekly → 13 weeks back) so
 * users don't have to reason about two overlapping filters.
 */
export const PERIOD_LOOKBACK_MONTHS: Record<string, number> = {
  daily: 1,      // ~30 days
  weekly: 3,     // ~13 weeks
  monthly: 12,
  quarterly: 24, // 8 quarters
}

export function monthsAgoISO(months: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() - months)
  return d.toISOString().slice(0, 10)
}

/** Build previousPoint map from a trends object. */
export function buildPreviousPointMap(
  trends: Record<MetricType, TrendPoint[]> | undefined,
): Map<MetricType, TrendPoint> {
  const map = new Map<MetricType, TrendPoint>()
  if (!trends) return map
  for (const [metric, points] of Object.entries(trends) as Array<[MetricType, TrendPoint[]]>) {
    if (points.length >= 2) map.set(metric, points[points.length - 2])
  }
  return map
}

/** Map latest snapshots by metric type. */
export function buildLatestMap(
  latest: DashboardSnapshot[] | undefined,
): Map<MetricType, DashboardSnapshot> {
  const map = new Map<MetricType, DashboardSnapshot>()
  latest?.forEach((s) => map.set(s.metric_type, s))
  return map
}
